import {Component, OnDestroy, OnInit} from '@angular/core';
import {ActivatedRoute, NavigationEnd, Router} from '@angular/router';
import {FormControl, FormGroup} from '@angular/forms';
import {SpecialistViewInterface} from '@interfaces/specialist.interface';
import {SpecialistEnums} from '@namespaces/specialist.namespace';
import {Subscription} from 'rxjs';
import {filter, skip, take} from 'rxjs/operators';
import {SpecialistsProvider} from '@providers/specialists/specialists.provider';
import {SpecialistService} from '../services/specialist.service';
import {AuthService} from '@services/auth.service';
import {UserNamespace} from '@namespaces/user.namespace';
import {EventsService} from '@services/events.service';
import {AjaxDownloadService} from '@components/ajax-download-noty/ajax-download.service';
import {RoutesStorage} from '@helpers/routes.helper';

@Component({
    selector: 'app-specialist-list',
    templateUrl: './specialist-list.component.html',
    styleUrls: ['./specialist-list.component.scss'],
    providers: []
})
export class SpecialistListComponent implements OnInit, OnDestroy {
    public permissions = UserNamespace.Permission;
    public specialists: SpecView[] = [];
    public filteredSpecialists: SpecView[] = [];

    public filters = new FormGroup({
        status: new FormControl('notFired')
    });

    public statusFilterValues = [
        {id: 'notFired', title: 'Действующие сотрудники'}
    ];

    valuesToRemove = [];

    public allowRemove = false;
    public canViewFired = false;

    public isFilterFiredActive = false;

    constructor(
        private route: ActivatedRoute,
        private router: Router,
        private service: SpecialistService,
        private specialistsProvider: SpecialistsProvider,
        private authService: AuthService,
        private eventsService: EventsService,
        private ajaxDownloadService: AjaxDownloadService,
    ) {}

    ngOnInit() {
        const { specialists } = this.route.snapshot.data;

        if (specialists) {
            this.handleSpecialistsReceive(specialists);
        }

        this.allowRemove = this.authService.hasPermissions([UserNamespace.Permission.specialistSetStatus]);
        this.canViewFired = this.authService.hasPermissions([UserNamespace.Permission.specialistViewFired]);

        if (this.canViewFired) {
            this.statusFilterValues.push({id: 'fired', title: 'Уволенные сотрудники'});
        } else {
            this.filters.get('status').disable();
        }

        this.checkFilters();
    }

    toggleSelection() {
        if (this.valuesToRemove.length === this.filteredSpecialists.length) {
            this.clearAll();
        } else {
            this.selectAll();
        }
    }

    onItemsSelectionChange(itemId: number, checked: boolean) {
        if (checked && !this.valuesToRemove.includes(itemId)) {
            this.valuesToRemove.push(itemId);
        }

        if (!checked && this.valuesToRemove.includes(itemId)) {
            this.valuesToRemove.splice(this.valuesToRemove.indexOf(itemId), 1);
        }
    }

    private selectAll() {
        this.valuesToRemove = this.filteredSpecialists.map(item => item.id);
    }

    clearAll() {
        this.valuesToRemove = [];
    }

    removeItems() {
        if (!this.allowRemove) {
            return;
        }

        this.service.post({
            url: this.service.fireUrl,
            dataBody: {
                id: this.valuesToRemove
            }
        }).toPromise().then(data => {

            this.eventsService.showNoty({
                type: 'success',
                content: 'Выбранные специалисты уволены',
            });

            this.valuesToRemove = [];

            this.specialistsProvider.data$.pipe(
                skip(1),
                take(1)
            ).subscribe(list => {
                this.handleSpecialistsReceive(list);
                this.applyFilters();
            });

            this.specialistsProvider.updateData();
        }, (errors: string[]) => {
            let message = 'Не удалось уволить выбранных специалистов';

            if (errors && errors.length) {
                message = errors.join('<br>');
            }

            this.eventsService.showNoty({
                type: 'warning',
                content: message,
            });
        });
    }

    applyFilters() {
        this.clearAll();
        const filtersValue = this.filters.value;
        this.filteredSpecialists = this.specialists.filter(s => {
            return filtersValue.status === 'notFired' ?
                +s.status !== SpecialistEnums.Status.fired :
                +s.status === SpecialistEnums.Status.fired;
        });
        this.checkFilters();
    }

    resetFilters() {
        this.filters.patchValue({
            status: 'notFired'
        });

        this.applyFilters();
    }

    handleSpecialistsReceive(list: SpecialistViewInterface[]) {
        this.specialists = list.map(spec => {
            return {
                ...spec,
                certificate_nk: spec.certificate_nk.map(cert => {
                    return {
                        number: cert.number,
                        date_end: cert.date_end,
                        methods: cert.methods.map(met => met.short_title)
                    };
                })
            };
        });

        this.applyFilters();
    }

    generateAttestationScheduleDocument() {
        this.service.get({
            url: this.service.validateAttestationScheduleDocumentUrl,
        }).subscribe(() => {
            this.ajaxDownloadService.do({
                url: this.service.generateAttestationScheduleDocumentUrl,
                taskName: 'График аттестации персонала',
                method: 'get',
            });
        }, (errors: string[]) => {
            let message = 'Возникла ошибка при формировании графика аттестации';

            if (errors && errors.length) {
                message = errors.join('<br>');
            }

            this.eventsService.showNoty({
                type: 'error',
                content: message,
                controls: [
                    {
                        title: 'Перейти в настройки',
                        callback: () => this.router.navigateByUrl(RoutesStorage.Organization.edit)
                    }
                ]
            });
        });
    }

    ngOnDestroy() {

    }

    private checkFilters() {
        const filtersValue = this.filters.value;
        this.isFilterFiredActive = filtersValue.status === 'fired';
    }
}

interface SpecView extends SpecialistViewInterface {
    certificate_nk: any;
}
